/*
 * ====================================================================================
 * FILE: RentalPage.jsx
 * PURPOSE: Rentals hub — a single page that switches between contract, machine,
 *          customer and invoice views via a KPI strip.
 *
 * VIEWS (driven by the KPI cards):
 *   all / active / completed → rental contracts
 *   ready / dehired          → fleet machines by lifecycle status (from DeviceStore)
 *   customers / activeCustomers → customer directory
 *   invoices                 → generated invoices
 *
 * Implements the "Rentals" Claude-Design layout (compact KPI view-switcher,
 * status pills, customer initials, count badge, search-off empty state) on the
 * app's theme tokens, wired to the real rental / customer / billing / device APIs.
 * (The mock "Purchase Orders" view from the design is omitted — there is no PO
 * backend; adding it would be a dead view.)
 * ====================================================================================
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setContractOrigin, originForPath } from '../../utils/contractOrigin';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Stack,
    TextField,
    InputAdornment,
    Divider,
    Skeleton,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Slide,
    ToggleButton,
    ToggleButtonGroup,
    Chip,
    ClickAwayListener,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Tooltip,
    Autocomplete,
    Snackbar,
} from '@mui/material';
import {
    Search as SearchIcon,
    SearchOff as SearchOffIcon,
    Add as AddIcon,
    GetApp as DownloadIcon,
    PersonAddAlt1 as PersonAddIcon,
    LocationOn as LocationIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Visibility as EyeIcon,
    Edit as EditIcon,
    Close as CloseIcon,
    Print as PrintIcon,
    FormatListBulleted as ListIcon,
    Description as ContractIcon,
    DownloadForOffline as InvoiceDownloadIcon,
    AddCircleOutline as AddCircleIcon,
    RemoveCircleOutline as RemoveCircleIcon,
    Undo as UndoIcon,
    EventAvailable as ReadyIcon,
    DoNotDisturbOn as DehiredIcon,
    PrecisionManufacturing as FleetIcon,
    Groups as GroupsIcon,
    HowToReg as ActiveCustIcon,
    ReceiptLong as InvoiceIcon,
    Assessment as ReportIcon,
    Engineering as EngineeringIcon,
    WbIncandescent as WbIncandescentIcon,
    BatteryChargingFull as BatteryChargingFullIcon,
} from '@mui/icons-material';
import mewpService from '../../services/mewpService';
import DailyReportPage from './DailyReportPage';
import PdfPreviewDialog from '../ui/PdfPreviewDialog';
import { formatToIST } from '../../utils/dataUtils';
import { useDeviceStore } from '../../store/DeviceStore';
import { statusLabel } from '../../utils/statusLabels';
import { parseDeviceId } from '../../utils/deviceUtils';

/*
 * Centralised status colour maps (DESIGN_GUIDE §3/§6) — values mirror the theme
 * palette tokens (primary/success/info/error + slate).
 */
const CONTRACT_STATUS = {
    Active:    { dot: '#2563eb', bg: '#dbeafe', text: '#1d4ed8' },
    Completed: { dot: '#10b981', bg: '#d1fae5', text: '#059669' },
    Draft:     { dot: '#94a3b8', bg: '#e2e8f0', text: '#475569' },
    Cancelled: { dot: '#ef4444', bg: '#fee2e2', text: '#b91c1c' },
};
const CUSTOMER_STATUS = {
    Active:   { dot: '#10b981', bg: '#d1fae5', text: '#059669' },
    Inactive: { dot: '#94a3b8', bg: '#e2e8f0', text: '#475569' },
};
const INVOICE_STATUS = {
    Paid:   { dot: '#10b981', bg: '#d1fae5', text: '#059669' },
    Unpaid: { dot: '#ef4444', bg: '#fee2e2', text: '#b91c1c' },
};
const MACHINE_STATUS = {
    'Ready for Rent': { dot: '#0ea5e9', bg: '#e0f2fe', text: '#0369a1' },
    'De-hired':       { dot: '#94a3b8', bg: '#e2e8f0', text: '#475569' },
};
const NEUTRAL_PILL = { dot: '#94a3b8', bg: '#e2e8f0', text: '#475569' };

const CATEGORY_STYLE = {
    'MEWP':         { color: '#2563eb', Icon: EngineeringIcon },
    'Tower Light':  { color: '#f59e0b', Icon: WbIncandescentIcon },
    'Battery Pack': { color: '#10b981', Icon: BatteryChargingFullIcon },
};
const categoryStyle = (cat) => CATEGORY_STYLE[cat] || { color: '#2563eb', Icon: EngineeringIcon };

const ROWS_PER_PAGE = 50;
const ACCENT_SOFT = 'rgba(37,99,235,0.08)';

// --- Small helpers ---------------------------------------------------------
const initials = (name) =>
    (name || '').split(/[\s&]+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

const fmtDate = (d) => (d ? formatToIST(d, 'dd MMM yyyy') : '—');

/* 'YYYY-MM-DD' (IST) for any date the API hands back — plain DATE strings are
   taken as-is, timestamps are converted to the IST calendar day so range
   comparisons match what the user sees on screen. */
const dayKey = (d) => {
    if (!d) return null;
    if (typeof d === 'string' && !d.includes('T') && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
    const s = formatToIST(d, 'yyyy-MM-dd');
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};

/** Local (not UTC) 'YYYY-MM-DD' — for the date inputs' defaults. */
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const csvEscape = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (filename, headers, lines) => {
    const csv = [headers.join(','), ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    URL.revokeObjectURL(url);
};

const rentalTypeLabel = (t) => {
    const k = String(t || '').toUpperCase();
    if (k === 'MONTHLY') return 'Monthly';
    if (k === 'USAGE_BASED' || k === 'USAGE') return 'Usage based';
    if (k === 'HOURLY') return 'Hourly';
    return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : 'Rental';
};

const contractStatusLabel = (s, fallback) => {
    const k = String(s || '').toUpperCase();
    if (k === 'ACTIVE') return 'Active';
    if (k === 'COMPLETED') return 'Completed';
    if (k === 'DRAFT') return 'Draft';
    if (k === 'CANCELLED') return 'Cancelled';
    return fallback || (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : 'Active');
};

/** Status pill — coloured dot + label (colour never carries meaning alone). */
const Pill = ({ s = NEUTRAL_PILL, label }) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: '8px', bgcolor: s.bg }}>
        <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: s.dot }} />
        <Typography component="span" sx={{ fontWeight: 700, fontSize: '11px', color: s.text, whiteSpace: 'nowrap' }}>{label}</Typography>
    </Box>
);

/**
 * `embedded` + `forcedView` let another page show ONE of this page's views as
 * its own section — the same component, so the two can never drift apart.
 * Log Sheets (DailyReportPage) uses it for the invoice downloader:
 *     <RentalPage embedded forcedView="invoiceDownload" />
 * Embedded drops the outer padding and the KPI/view switcher strip (the host
 * page does the navigating), and the forced view can't be changed from the URL.
 *
 * `hideViewStrip` drops just the KPI/view switcher while keeping the full page
 * padding — for a department page that pins one view and navigates from the
 * top bar instead (Operations → Machine Contracts).
 */
const RentalPage = ({ embedded = false, forcedView = null, hideViewStrip = false }) => {
    const navigate = useNavigate();
    const scrollRef = useRef(null);

    const handleScroll = (direction) => {
        if (scrollRef.current) {
            const scrollAmount = 300;
            scrollRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    // View resolution order:
    //   1. /rentals?view=…            (breadcrumbs deep-link a specific card)
    //   2. saved return-view          (set when drilling into a detail page, so
    //                                  coming BACK restores the card you were on)
    //   3. dailyReport                (fresh entry → the Rental Daily Report)
    const [searchParams] = useSearchParams();
    const VIEWS = useMemo(() => ['dailyReport', 'all', 'active', 'completed', 'invoiceDownload', 'ready', 'dehired', 'fleet', 'customers', 'activeCustomers', 'invoices', 'contractData', 'machineContracts'], []);
    const RETURN_KEY = 'rentalsReturnView';
    const initialView = (() => {
        if (forcedView && VIEWS.includes(forcedView)) return forcedView;
        const p = searchParams.get('view');
        if (VIEWS.includes(p)) return p;
        const saved = sessionStorage.getItem(RETURN_KEY);
        sessionStorage.removeItem(RETURN_KEY); // consume — navbar entries stay fresh
        if (VIEWS.includes(saved)) return saved;
        return 'dailyReport';
    })();
    const [view, setView] = useState(initialView);
    // The Sales/Operations top-bar headers deep-link cards on this same page
    // (?view=…) — follow changes while mounted (same route, no remount).
    useEffect(() => {
        if (forcedView) return;                 // host page owns the view
        const p = searchParams.get('view');
        if (p && VIEWS.includes(p)) setView(p);
    }, [searchParams, VIEWS, forcedView]);
    // Remember the current card when drilling into a detail page.
    const goDetail = (path) => {
        sessionStorage.setItem(RETURN_KEY, view);
        setContractOrigin(originForPath(window.location.pathname));   // breadcrumbs lead back to THIS door
        navigate(path);
    };

    const [page, setPage] = useState(1);

    const [contracts, setContracts] = useState([]);
    const [completedData, setCompletedData] = useState([]);
    const [fleetCount, setFleetCount] = useState(0);
    const [invoices, setInvoices] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [machineContracts, setMachineContracts] = useState([]); // all ced rows across contracts

    // --- Invoice Download filters (view === 'invoiceDownload') ---------------
    // Default window = the current month to date.
    const [invFrom, setInvFrom] = useState(() => isoDay(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
    const [invTo, setInvTo] = useState(() => isoDay(new Date()));
    const [invScope, setInvScope] = useState('all');           // 'all' | 'selected'
    const [invSelected, setInvSelected] = useState([]);         // equipment_ids
    /* Sites picked in the same search box. A site is NOT expanded into a fixed
       list of machines: it stays a site, and the machines under it are derived
       from the date window on every render, so changing From/To re-evaluates
       which machines worked there without the user re-picking anything. */
    const [invSelectedSites, setInvSelectedSites] = useState([]);
    /* What the Export button produces. 'invoice' uses the current report
       template and is the working path; 'log' is reserved for the separate log
       format still to be supplied, which will be added to file_templates. */
    const [dlKind, setDlKind] = useState('invoice');           // 'invoice' | 'log'
    const [invSearch, setInvSearch] = useState('');
    const [invSearchOpen, setInvSearchOpen] = useState(false);
    /* Rows the user removed from this list with the row's remove button, by row
       key (machine + contract). Removals apply to whatever the list is built
       from — a site pick, an explicit machine, or "All machines" — and they
       carry straight into the Export/CSV, which is the point: drop a machine you
       don't want invoiced. Always reversible from the "N removed" chip. */
    const [invRemoved, setInvRemoved] = useState([]);

    /* Invoice download — the SAME per-machine usage/worklog PDF the contract
       live view produces (GET /downloads/device-summary/pdf/:id), one file per
       machine per rental window, named by serial. Same options, same template. */
    const [dlOpen, setDlOpen] = useState(false);
    const [dlFrom, setDlFrom] = useState('');
    const [dlTo, setDlTo] = useState('');
    const [dlLeverage, setDlLeverage] = useState('2');       // grace hours on the shift start
    const [dlOtBilling, setDlOtBilling] = useState('actual'); // OT billing scale
    const [dlBusy, setDlBusy] = useState(false);
    const [dlError, setDlError] = useState('');
    const [dlProgress, setDlProgress] = useState(null);       // { done, total, saved, finished }
    const [docPreview, setDocPreview] = useState(null);       // one machine's invoice, shown in place

    // Fleet machines (Ready for Rent / De-hired) come from the global device store.
    const { state: deviceState } = useDeviceStore();
    const { devices, tracking: trackingData, loading: devicesLoading } = deviceState;

    // Invoice preview modal
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [templateHtml, setTemplateHtml] = useState('');
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState('');

    // Add new customer modal
    const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
    const [newCustomerForm, setNewCustomerForm] = useState({
        company_name: '', contact_person: '', email: '', phone: '', address: '', gst_no: '', status: 'ACTIVE'
    });
    const [savingNewCustomer, setSavingNewCustomer] = useState(false);

    // Customer details / edit modal
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [customerForm, setCustomerForm] = useState({
        company_name: '', contact_person: '', email: '', phone: '', address: '', gst_no: '', status: 'ACTIVE'
    });

    // New contract modal
    const [showNewContractModal, setShowNewContractModal] = useState(false);
    const [creatingContract, setCreatingContract] = useState(false); // guards double-submit
    // Contract header only — machines are mapped afterwards in Machine Contracts,
    // each with its own rental start date (that date drives its invoice).
    const [newContractForm, setNewContractForm] = useState({
        rental_contract_id: '', customer_id: '', contract_start: '', contract_end: '',
        rental_type: 'MONTHLY', site_location: '',
    });

    /* ---- Add machine contract (map a machine onto an existing contract) ---- */
    const [showAddMachineContract, setShowAddMachineContract] = useState(false);
    const [savingMachineContract, setSavingMachineContract] = useState(false);
    const [snack, setSnack] = useState('');
    const BLANK_MC = {
        rental_contract_id: '', equipment_id: '', effective_from_date: '', effective_to_date: '',
        shifts: [{ start_time: '08:00', end_time: '18:00' }],
        monthly_rate: 0, hourly_rate: 0, extra_hours_rate: 0, advance_payment: 0,
    };
    const [mcForm, setMcForm] = useState(BLANK_MC);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await mewpService.getContracts('ACTIVE');
            if (response?.success) setContracts(response.data || []);
        } catch (error) { console.error('Error fetching contracts:', error); }

        try {
            const mcRes = await mewpService.getAllMachineContracts();
            if (mcRes?.success) setMachineContracts(mcRes.machine_contracts || []);
        } catch (error) { console.error('Error fetching machine contracts:', error); setMachineContracts([]); }

        try {
            const completedResponse = await mewpService.getCompletedRentals();
            if (completedResponse?.success && Array.isArray(completedResponse.data)) setCompletedData(completedResponse.data);
        } catch (error) { console.error('Error fetching completed rentals:', error); setCompletedData([]); }

        try {
            const fleetRes = await mewpService.getFleetStats();
            if (fleetRes?.success) setFleetCount(fleetRes.data.total_fleet || 0);
        } catch (error) { console.error('Error fetching fleet stats:', error); }

        try {
            const invResponse = await mewpService.getInvoices();
            if (invResponse?.success && Array.isArray(invResponse.data)) setInvoices(invResponse.data);
        } catch (error) { console.error('Error fetching invoices:', error); setInvoices([]); }

        try {
            const custResponse = await mewpService.getCustomers();
            if (custResponse?.success) setCustomers(custResponse.data || []);
        } catch (error) {
            console.error('Error fetching customers:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);
    // Reset to page 1 when the view or search changes.
    useEffect(() => { setPage(1); }, [view, searchTerm, invFrom, invTo, invScope, invSelected, invSelectedSites]);

    const handleViewInvoice = async (invoiceId) => {
        setModalLoading(true);
        setModalError('');
        setTemplateHtml('');
        setShowInvoiceModal(true);
        try {
            const response = await mewpService.getInvoiceDetail(invoiceId);
            if (response.success) {
                const { invoice, template } = response.data;
                setSelectedInvoice(invoice);

                let rendered = template?.template_data;
                if (!rendered) {
                    setModalError('Template data not found for this invoice. Please ensure templates are seeded in the database.');
                    setModalLoading(false);
                    return;
                }

                let totalAmount = 0;
                let totalQty = 0;
                let itemsHtml = '';

                if (invoice.line_items_json) {
                    const items = typeof invoice.line_items_json === 'string' ? JSON.parse(invoice.line_items_json) : invoice.line_items_json;
                    items.forEach((item, idx) => {
                        const amt = parseFloat(item.amount) || 0;
                        const qty = parseFloat(item.qty) || 0;
                        totalAmount += amt;
                        totalQty += qty;
                        itemsHtml += `
                            <tr style="border-bottom: 1px solid #000;">
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: center;">${idx + 1}</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: center;">${item.model || ''}<br>${item.serial || ''}</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: center;">9973</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: left;">${item.description}</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: center;">Nos</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: center;">${qty}</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: right;">${parseFloat(item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: right;">${amt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: right;">0.00</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: center;">18%</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: right;">${(amt * 0.09).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: right;">${(amt * 0.09).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td style="padding: 5px; text-align: right;">0.00</td>
                            </tr>
                        `;
                    });
                }

                const cgst = totalAmount * 0.09;
                const sgst = totalAmount * 0.09;
                const grandTotal = totalAmount + cgst + sgst;
                const formatNum = (num) => num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                const numberToWords = (amount) => 'INR ' + formatNum(amount) + ' Only';

                rendered = rendered.replace(/{{branch}}/g, invoice.branch || 'Padur');
                rendered = rendered.replace(/{{billed_to_name}}/g, invoice.contact_name || invoice.customer || '-');
                rendered = rendered.replace(/{{billed_to_address}}/g, invoice.billed_to_address || '-');
                rendered = rendered.replace(/{{billed_to_pin}}/g, '');
                rendered = rendered.replace(/{{billed_to_state}}/g, 'TAMILNADU');
                rendered = rendered.replace(/{{billed_to_state_code}}/g, '33');
                rendered = rendered.replace(/{{billed_to_gstin}}/g, '');
                rendered = rendered.replace(/{{billed_to_pan}}/g, '');
                rendered = rendered.replace(/{{ship_to_name}}/g, invoice.contact_name || invoice.customer || '-');
                rendered = rendered.replace(/{{ship_to_address}}/g, invoice.ship_to_address || invoice.billed_to_address || '-');
                rendered = rendered.replace(/{{ship_to_state}}/g, 'TAMILNADU');
                rendered = rendered.replace(/{{ship_to_state_code}}/g, '33');
                rendered = rendered.replace(/{{ship_to_pin}}/g, '');
                rendered = rendered.replace(/{{ship_to_gstin}}/g, '');
                rendered = rendered.replace(/{{bill_no}}/g, invoice.bill_no || '-');
                rendered = rendered.replace(/{{bill_date}}/g, invoice.bill_date ? formatToIST(invoice.bill_date).split(',')[0] : '-');
                rendered = rendered.replace(/{{dc_no}}/g, invoice.dc_no || '-');
                rendered = rendered.replace(/{{dc_date}}/g, invoice.dc_date ? formatToIST(invoice.dc_date).split(',')[0] : '-');
                rendered = rendered.replace(/{{lr_date}}/g, invoice.lr_date ? formatToIST(invoice.lr_date).split(',')[0] : '-');
                rendered = rendered.replace(/{{transporter}}/g, invoice.transporter || '-');
                rendered = rendered.replace(/{{buyer_order_no}}/g, invoice.buyer_order_no || '-');
                rendered = rendered.replace(/{{vehicle_no}}/g, invoice.vehicle_no || '-');
                rendered = rendered.replace(/{{buyer_order_dt}}/g, invoice.buyer_order_date ? formatToIST(invoice.buyer_order_date).split(',')[0] : '-');
                rendered = rendered.replace(/{{contact_name}}/g, invoice.contact_name || '-');
                rendered = rendered.replace(/{{contact_phone}}/g, invoice.contact_phone || '-');
                rendered = rendered.replace(/{{qr_code_image}}/g, '-');
                rendered = rendered.replace(/{{items_table_rows}}/g, itemsHtml);
                rendered = rendered.replace(/{{total_qty}}/g, totalQty.toString());
                rendered = rendered.replace(/{{total_taxable_amount}}/g, formatNum(totalAmount));
                rendered = rendered.replace(/{{total_cgst}}/g, formatNum(cgst));
                rendered = rendered.replace(/{{total_sgst}}/g, formatNum(sgst));
                rendered = rendered.replace(/{{total_igst}}/g, '0.00');
                rendered = rendered.replace(/{{amount_in_words}}/g, numberToWords(Math.round(grandTotal)));
                rendered = rendered.replace(/{{total_taxable_amount_formatted}}/g, formatNum(totalAmount));
                rendered = rendered.replace(/{{total_cgst_formatted}}/g, formatNum(cgst));
                rendered = rendered.replace(/{{total_sgst_formatted}}/g, formatNum(sgst));
                rendered = rendered.replace(/{{total_igst_formatted}}/g, '0.00');
                rendered = rendered.replace(/{{grand_total_formatted}}/g, formatNum(grandTotal));
                rendered = rendered.replace(/{{authorized_signatory}}/g, invoice.authorized_signatory || 'JENNY MODI');
                rendered = rendered.replace(/{{irn_no}}/g, invoice.irn_no || '-');
                rendered = rendered.replace(/{{ack_no}}/g, '');
                rendered = rendered.replace(/{{ack_date}}/g, invoice.ack_no_date || '');

                setTemplateHtml(rendered);
            }
        } catch (error) {
            console.error('Error showing invoice:', error);
            setModalError(error.message || 'Failed to load invoice. Please try again.');
        } finally {
            setModalLoading(false);
        }
    };

    // --- Derived data ---
    const allContracts = useMemo(() => [...contracts, ...completedData], [contracts, completedData]);

    // Contracts per customer (active + completed currently loaded) for the directory badge.
    const contractCountByCustomer = useMemo(() => {
        const m = {};
        allContracts.forEach(c => { if (c.customer_id != null) m[c.customer_id] = (m[c.customer_id] || 0) + 1; });
        return m;
    }, [allContracts]);

    // Fleet machines by lifecycle status (mirror of the Device Management merge).
    const machines = useMemo(() => {
        const mewpCategories = ['Boom Lift', 'Mast Boom - SP', 'Scissor Lift', 'Spider Lift', 'Vertical Lift', 'Vertical Mast', 'Telehandler', 'Crane'];
        return devices.map(device => {
            const tracking = trackingData.find(t => t.equipment_id === device.equipment_id || t.equipment_id === device.device_id) || null;
            const info = parseDeviceId(device.device_id);
            const prod = device.product_category || 'MEWP';
            const isTest = device.is_test || prod === 'Test Device' || /^TEST[-_]/i.test(device.device_id || '') || /^TEST[-_]/i.test(device.equipment_id || '');
            const category = isTest ? 'Test Device' : (mewpCategories.includes(prod) ? 'MEWP' : (info.type !== 'Unknown' ? info.type : prod));
            const status = tracking ? statusLabel(tracking.status) : 'TCU not installed';
            return {
                ...device,
                model: device.model_number || device.name || device.equipment_id,
                sub: prod,
                assetId: device.equipment_id || device.device_id,
                brand: device.brand || '—',
                category,
                status,
                place: device.location_name || (tracking?.site_location) || '—',
            };
        }).filter(m => m.category !== 'Test Device');
    }, [devices, trackingData]);

    const readyMachines = useMemo(() => machines.filter(m => m.status === 'Ready for Rent'), [machines]);
    const dehiredMachines = useMemo(() => machines.filter(m => m.status === 'De-hired'), [machines]);
    const activeCustomers = useMemo(() => customers.filter(c => String(c.status).toUpperCase() === 'ACTIVE'), [customers]);

    // ---- Invoice Download -------------------------------------------------
    /** rental_contract_id → customer / site / contract window (active + completed). */
    const contractMeta = useMemo(() => {
        const m = {};
        contracts.forEach(c => {
            if (!c.rental_contract_id) return;
            m[c.rental_contract_id] = {
                customer: c.company_name || c.contact_name || '',
                location: c.site_location || '',
                start: c.contract_start || c.contract_start_date || null,
                end: c.contract_end || c.contract_end_date || null,
            };
        });
        completedData.forEach(c => {
            if (!c.rental_contract_id || m[c.rental_contract_id]) return;
            m[c.rental_contract_id] = {
                customer: c.contact_name || c.company_name || '',
                location: c.site_location || '',
                start: c.contract_start_date || c.contract_start || null,
                end: c.contract_end_date || c.contract_end || null,
            };
        });
        return m;
    }, [contracts, completedData]);

    /** equipment_id → fleet machine (serial / model / brand / location). */
    const machineById = useMemo(() => {
        const m = {};
        machines.forEach(x => { if (x.assetId) m[x.assetId] = x; });
        return m;
    }, [machines]);

    /* One row per machine × contract that was on rent at any point inside the
       [from, to] window. A machine-contract line with no end date is still
       running, so it always overlaps the right-hand edge. Amendment lines for
       the same machine+contract are merged into the widest window. */
    const invRentalRows = useMemo(() => {
        const byKey = new Map();
        machineContracts.forEach(mc => {
            if (String(mc.status || '').toUpperCase() === 'CANCELLED') return;
            // A DRAFT / CANCELLED contract is a quote, not a rental — it must never
            // reach this list, or the machine gets invoiced for a job it never did.
            if (['DRAFT', 'PENDING', 'QUOTED', 'CANCELLED'].includes(String(mc.contract_status || '').toUpperCase())) return;
            const meta = contractMeta[mc.rental_contract_id] || {};
            const start = dayKey(mc.effective_from_date) || dayKey(meta.start) || dayKey(mc.contract_start);
            const end = dayKey(mc.effective_to_date) || dayKey(meta.end) || dayKey(mc.contract_end); // null → ongoing
            if (invTo && start && start > invTo) return;
            if (invFrom && end && end < invFrom) return;
            const dev = machineById[mc.equipment_id] || {};
            const key = `${mc.equipment_id}|${mc.rental_contract_id}`;
            const row = {
                key,
                equipment_id: mc.equipment_id,
                serial_number: dev.serial_number || mc.serial_number || '',
                model: mc.model_number || dev.model || '',
                brand: mc.brand || dev.brand || '',
                location: meta.location || mc.site_location || dev.place || '',
                customer: meta.customer || mc.company_name || '',
                rental_contract_id: mc.rental_contract_id || '',
                start, end,
            };
            const prev = byKey.get(key);
            byKey.set(key, prev ? {
                ...prev,
                start: prev.start && row.start ? (prev.start < row.start ? prev.start : row.start) : (prev.start || row.start),
                end: prev.end && row.end ? (prev.end > row.end ? prev.end : row.end) : null,
            } : row);
        });
        return [...byKey.values()].sort((a, b) =>
            String(a.serial_number || a.equipment_id).localeCompare(String(b.serial_number || b.equipment_id)));
    }, [machineContracts, contractMeta, machineById, invFrom, invTo]);

    /* Machine picker catalogue — machines on rent in the window first, then the
       rest of the fleet (so a machine can be picked even with no rental line). */
    const invCatalogue = useMemo(() => {
        const byId = new Map();
        invRentalRows.forEach(r => { if (!byId.has(r.equipment_id)) byId.set(r.equipment_id, r); });
        machines.forEach(m => {
            if (byId.has(m.assetId)) return;
            byId.set(m.assetId, {
                key: m.assetId, equipment_id: m.assetId,
                serial_number: m.serial_number || '', model: m.model || '', brand: m.brand || '',
                location: m.place || '', customer: '', rental_contract_id: '', start: null, end: null,
            });
        });
        return [...byId.values()];
    }, [invRentalRows, machines]);

    /* Site catalogue for the picker. Built from every contract, NOT from the
       date-filtered rows, so a site stays findable even when the current window
       has no work at it — you'd otherwise type a site you know exists and get
       "no match" purely because the dates were wrong. */
    const normSite = (s) => String(s || '').trim().toLowerCase();
    const invSites = useMemo(() => {
        const m = new Map();
        allContracts.forEach(c => {
            const site = String(c.site_location || '').trim();
            if (!site) return;
            const k = normSite(site);
            if (!m.has(k)) m.set(k, { site, customers: new Set(), contracts: new Set() });
            if (c.company_name) m.get(k).customers.add(c.company_name);
            if (c.rental_contract_id) m.get(k).contracts.add(c.rental_contract_id);
        });
        return [...m.values()].map(s => ({
            site: s.site,
            customer: [...s.customers].join(', '),
            contractCount: s.contracts.size,
        })).sort((a, b) => a.site.localeCompare(b.site));
    }, [allContracts]);

    /* Machines that worked at the selected sites INSIDE the current window.
       Derived from invRentalRows (already clipped to From/To), so this list
       re-evaluates automatically whenever the dates change. */
    const invSiteRows = useMemo(() => {
        if (!invSelectedSites.length) return [];
        const want = new Set(invSelectedSites.map(normSite));
        return invRentalRows.filter(r => want.has(normSite(r.location)));
    }, [invSelectedSites, invRentalRows]);

    /* Picker search — sites and machines in ONE result list. Sites first, since
       picking a site is the broader action. Machines match on their own identity
       plus who they are out with (customer/site/contract). */
    const invSearchResults = useMemo(() => {
        const q = invSearch.trim().toLowerCase();
        if (!q) return { sites: [], machines: [] };
        return {
            sites: invSites
                .filter(s => [s.site, s.customer].some(x => (x || '').toLowerCase().includes(q)))
                .slice(0, 8),
            machines: invCatalogue
                .filter(m => [m.serial_number, m.model, m.brand, m.equipment_id,
                              m.customer, m.location, m.rental_contract_id]
                    .some(x => (x || '').toLowerCase().includes(q)))
                .slice(0, 25),
        };
    }, [invSearch, invSites, invCatalogue]);

    /* Rows for the view: every rental in the window, or the picked machines plus
       everything under the picked sites. A machine picked explicitly that had no
       rental in the window still shows, so an empty selection is visible rather
       than silently dropped. */
    const invRows = useMemo(() => {
        const dropped = new Set(invRemoved);
        const keep = (list) => list.filter(r => !dropped.has(r.key));
        if (invScope === 'all') return keep(invRentalRows);
        const sel = new Set(invSelected);
        const wantSites = new Set(invSelectedSites.map(normSite));
        const picked = invRentalRows.filter(r =>
            sel.has(r.equipment_id) || wantSites.has(normSite(r.location)));
        const covered = new Set(picked.map(r => r.equipment_id));
        const extras = invSelected.filter(id => !covered.has(id)).map(id => {
            const c = invCatalogue.find(x => x.equipment_id === id);
            return { ...(c || { equipment_id: id, serial_number: '', model: '', brand: '', location: '', customer: '', rental_contract_id: '', start: null, end: null }), key: `sel-${id}` };
        });
        return keep([...picked, ...extras]);
    }, [invScope, invSelected, invSelectedSites, invRentalRows, invCatalogue, invRemoved]);

    const toggleInvMachine = (id) =>
        setInvSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

    /**
     * Remove one row from the invoice list. The row key (machine + contract) is
     * excluded, so a machine on two contracts loses only the line you removed;
     * an explicitly picked machine also drops out of the selection so it doesn't
     * reappear as an "extra" row.
     */
    const removeInvRow = (row) => {
        if (!row?.key) return;
        setInvRemoved(prev => (prev.includes(row.key) ? prev : [...prev, row.key]));
        setInvSelected(prev => prev.filter(id => id !== row.equipment_id));
    };
    const toggleInvSite = (site) =>
        setInvSelectedSites(prev => (prev.some(s => normSite(s) === normSite(site))
            ? prev.filter(s => normSite(s) !== normSite(site))
            : [...prev, site]));

    const isContracts = view === 'active' || view === 'completed' || view === 'all' || view === 'contractData';
    const isMachines = view === 'ready' || view === 'dehired' || view === 'fleet';
    const isCustomers = view === 'customers' || view === 'activeCustomers';
    const isInvoices = view === 'invoices';
    const isMachineContracts = view === 'machineContracts';
    const isInvoiceDownload = view === 'invoiceDownload';

    const TITLES = {
        active: 'Active deployments', completed: 'Completed rentals', all: 'All rentals',
        invoiceDownload: 'Invoice download — machines on rent in the selected period',
        ready: 'Machines · Ready for rent', dehired: 'Machines · De-hired',
        fleet: 'Total fleet — all machines',
        customers: 'Customer directory', activeCustomers: 'Active customers', invoices: 'Generated invoices',
        contractData: 'Contract details — open a contract to inspect & correct its data',
        machineContracts: 'Machine contracts — every machine’s contract line',
    };

    // Rows for the active view (search-filtered).
    const rows = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (isContracts) {
            const base = view === 'active' ? contracts : view === 'completed' ? completedData : allContracts; // 'all' + 'contractData' show every contract
            return base.filter(c => !q || [c.rental_contract_id, c.company_name, c.site_location].some(x => (x || '').toLowerCase().includes(q)));
        }
        if (isMachines) {
            const base = view === 'ready' ? readyMachines : view === 'dehired' ? dehiredMachines : machines; // 'fleet' = every machine
            return base.filter(m => !q || [m.serial_number, m.model, m.assetId, m.brand].some(x => (x || '').toLowerCase().includes(q)));
        }
        if (isCustomers) {
            const base = view === 'activeCustomers' ? activeCustomers : customers;
            return base.filter(c => !q || [c.company_name, c.contact_person, c.email].some(x => (x || '').toLowerCase().includes(q)));
        }
        if (isInvoices) {
            return invoices.filter(c => !q || [c.bill_no, c.id, c.contact_name, c.customer, c.method].some(x => (x ? String(x) : '').toLowerCase().includes(q)));
        }
        if (isMachineContracts) {
            return machineContracts.filter(mc => !q || [mc.rental_contract_id, mc.sub_contract_id, mc.equipment_id, mc.serial_number].some(x => (x || '').toLowerCase().includes(q)));
        }
        if (isInvoiceDownload) {
            return invRows.filter(r => !q || [r.serial_number, r.model, r.brand, r.location, r.customer, r.equipment_id, r.rental_contract_id].some(x => (x || '').toLowerCase().includes(q)));
        }
        return [];
    }, [view, searchTerm, isContracts, isMachines, isCustomers, isInvoices, isMachineContracts, isInvoiceDownload, contracts, completedData, allContracts, readyMachines, dehiredMachines, machines, customers, activeCustomers, invoices, machineContracts, invRows]);

    const total = rows.length;
    const pageCount = Math.ceil(total / ROWS_PER_PAGE) || 1;
    const pagedRows = useMemo(() => rows.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE), [rows, page]);

    const showSkeleton = (loading && !isMachines) || (isMachines && devicesLoading && devices.length === 0);

    // KPI / view-switcher cards. The Daily Report card shows the report day
    // (yesterday) instead of a count.
    const yest = new Date(Date.now() - 86400000);
    const yestLabel = `${String(yest.getDate()).padStart(2, '0')} ${yest.toLocaleString('en-GB', { month: 'short' })}`;
    const kpiCards = [
        { key: 'dailyReport',     label: 'Daily Report',     value: yestLabel,                icon: <ReportIcon />,      color: '#0f766e', view: 'dailyReport' },
        { key: 'all',             label: 'All Rentals',      value: allContracts.length,      icon: <ListIcon />,        color: '#0ea5e9', view: 'all' },
        { key: 'active',          label: 'Active Rentals',   value: contracts.length,         icon: <ContractIcon />,    color: '#2563eb', view: 'active' },
        { key: 'invoiceDownload', label: 'Invoice Download', value: invRentalRows.length,     icon: <InvoiceDownloadIcon />, color: '#059669', view: 'invoiceDownload' },
        { key: 'ready',           label: 'Ready for Rent',   value: readyMachines.length,     icon: <ReadyIcon />,       color: '#0891b2', view: 'ready' },
        { key: 'dehired',         label: 'De-hired',         value: dehiredMachines.length,   icon: <DehiredIcon />,     color: '#64748b', view: 'dehired' },
        { key: 'fleet',           label: 'Total Fleet',      value: fleetCount,               icon: <FleetIcon />,       color: '#7c3aed', view: 'fleet' },
        { key: 'customers',       label: 'Customers',        value: customers.length,         icon: <GroupsIcon />,      color: '#ec4899', view: 'customers' },
        { key: 'contractData',    label: 'Contract Details', value: allContracts.length,      icon: <ContractIcon />,    color: '#4f46e5', view: 'contractData' },
        { key: 'machineContracts', label: 'Machine Contracts', value: machineContracts.length, icon: <FleetIcon />,      color: '#0e7490', view: 'machineContracts' },
        { key: 'invoices',        label: 'Invoices',         value: invoices.length,          icon: <InvoiceIcon />,     color: '#f59e0b', view: 'invoices' },
    ];

    const switchView = (v) => { if (!v) return; setView(v); setSearchTerm(''); };

    const openCustomer = (item) => {
        setSelectedCustomer(item);
        setCustomerForm({ ...item });
        setEditMode(false);
        setShowCustomerModal(true);
    };

    /** Open the rental-focused machine detail page (identity + last rental +
     *  last service + spec sheet) — not the device-management telemetry view. */
    const openMachine = (m) => {
        goDetail(`/rental-machine/${encodeURIComponent(m.assetId)}`);
    };

    /* ── Invoice download: one usage PDF per machine, same as the contract
       live view's "Download Work Logs" (same endpoint, same template). ────── */
    const saveBlobAsFile = (blob, filename) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    };

    /** Open the download dialog, pre-filled with the filter bar's date window. */
    const openInvoiceDownload = () => {
        setDlFrom(invFrom);
        setDlTo(invTo);
        setDlError('');
        setDlProgress(null);
        setDlOpen(true);
    };

    /**
     * One job per machine per rental window, clipped to the chosen dates: a
     * machine that was on rent only 01–04 Jun gets a 01–04 Jun report. Rows the
     * user picked that had no rental in the window fall back to the whole range.
     */
    const invoiceDownloadJobs = (fromDate, toDate) => {
        const seen = new Set();
        const jobs = [];
        for (const r of rows) {
            if (!r.equipment_id) continue;
            const s = r.start && r.start > fromDate ? r.start : fromDate;
            const e = r.end && r.end < toDate ? r.end : toDate;
            if (s > e) continue;
            const key = `${r.equipment_id}|${s}|${e}`;
            if (seen.has(key)) continue;
            seen.add(key);
            jobs.push({ id: r.equipment_id, serial: r.serial_number || r.equipment_id, from: s, to: e });
        }
        return jobs;
    };

    /**
     * A row in the Log Sheets list opens THAT machine's document for the dates
     * on screen — what the Invoice / Log toggle says — not its details page:
     * Invoice → the same usage PDF the Export makes, clipped to the machine's
     * rental window, previewed in place with a Download button; Log → the
     * machine's day-by-day log (/rental-log), since the printable Log format
     * is not defined yet.
     */
    const openMachineDoc = (r) => {
        if (!r.equipment_id) return;
        if (dlKind === 'log') {
            goDetail(`/rental-log/${encodeURIComponent(r.equipment_id)}` +
                `${r.rental_contract_id ? `?contractId=${encodeURIComponent(r.rental_contract_id)}` : ''}`);
            return;
        }
        const from = r.start && r.start > invFrom ? r.start : invFrom;
        const to = r.end && r.end < invTo ? r.end : invTo;
        const serial = r.serial_number || r.equipment_id;
        const baseUrl = process.env.REACT_APP_API_URL || 'https://api.mtandtiot.in/api';
        setDocPreview({
            url: `${baseUrl}/downloads/device-summary/pdf/${encodeURIComponent(r.equipment_id)}?from=${from}&to=${to}` +
                 `&leverage=${encodeURIComponent(dlLeverage || 0)}&otScale=${encodeURIComponent(dlOtBilling)}&skipEmpty=1`,
            title: `Invoice · ${serial} · ${fmtDate(from)} – ${fmtDate(to)}`,
            fallbackName: `${serial}_${from}_to_${to}.pdf`,
            emptyMessage: `${serial} did not work between ${fmtDate(from)} and ${fmtDate(to)}, so there is no invoice for these dates.`,
        });
    };

    const handleDownloadInvoices = async () => {
        // The separate Log format hasn't been supplied yet. Refuse rather than
        // hand back the Invoice document under a "Log" label — a wrong document
        // is worse than none when these go to customers.
        if (dlKind === 'log') {
            setDlError('The Log format is not defined yet. Send the layout and it will be added to the template table; use “Invoice” meanwhile.');
            return;
        }
        if (!dlFrom || !dlTo) { setDlError('Please choose both From and To dates.'); return; }
        if (dlFrom > dlTo) { setDlError('"From" date must be on or before "To" date.'); return; }

        const jobs = invoiceDownloadJobs(dlFrom, dlTo);
        if (!jobs.length) { setDlError('No machines in the list for those dates. Widen the range or add machines above.'); return; }

        const baseUrl = process.env.REACT_APP_API_URL || 'https://api.mtandtiot.in/api';
        const token = localStorage.getItem('token');

        setDlBusy(true); setDlError('');
        setDlProgress({ done: 0, total: jobs.length, saved: 0 });

        let saved = 0, done = 0;
        for (const job of jobs) {
            try {
                const res = await fetch(
                    `${baseUrl}/downloads/device-summary/pdf/${encodeURIComponent(job.id)}?from=${job.from}&to=${job.to}&leverage=${encodeURIComponent(dlLeverage || 0)}&otScale=${encodeURIComponent(dlOtBilling)}&skipEmpty=1`,
                    { headers: token ? { 'x-auth-token': token } : {} }
                );
                if (res.ok && res.status !== 204) {          // 204 = machine never worked in its window
                    const blob = await res.blob();
                    const head = await blob.slice(0, 4).text();
                    if (head.startsWith('%PDF')) {
                        const cd = res.headers.get('content-disposition') || '';
                        const m = cd.match(/filename="?([^"]+)"?/);
                        saveBlobAsFile(blob, m ? m[1] : `${job.serial}_${job.from}_to_${job.to}.pdf`);
                        saved++;
                        // small gap so the browser accepts several downloads in a row
                        await new Promise(r => setTimeout(r, 400));
                    }
                }
            } catch (e) { /* counted as not-saved below */ }
            done++;
            setDlProgress({ done, total: jobs.length, saved });
        }

        setDlBusy(false);
        if (saved === 0) {
            setDlError(`No machine worked between those dates (checked ${jobs.length}).`);
        } else {
            setDlProgress({ done, total: jobs.length, saved, finished: true });
            setTimeout(() => { setDlOpen(false); setDlProgress(null); }, 1800);
        }
    };

    /** Export the rows of the current view to a CSV download. */
    const handleExportLogs = () => {
        const stamp = new Date().toISOString().slice(0, 10);
        let headers = [];
        let lines = [];
        if (isContracts) {
            headers = ['Contract ID', 'Rental Type', 'Customer', 'Site', 'Units', 'Start', 'End', 'Status'];
            lines = rows.map(c => [c.rental_contract_id, rentalTypeLabel(c.rental_type), c.company_name, c.site_location, c.device_count || 0, fmtDate(c.contract_start), c.contract_end ? fmtDate(c.contract_end) : 'Open-end', contractStatusLabel(c.contract_status, view === 'completed' ? 'Completed' : 'Active')].map(csvEscape).join(','));
        } else if (isMachines) {
            headers = ['Serial No', 'Equipment ID', 'Model', 'Brand', 'Category', 'Location', 'Status'];
            lines = rows.map(m => [m.serial_number || '', m.assetId, m.model, m.brand, m.category, m.place, m.status].map(csvEscape).join(','));
        } else if (isInvoiceDownload) {
            headers = ['Serial No', 'Brand', 'Model', 'Location', 'Customer', 'Contract', 'On rent from', 'On rent to'];
            lines = rows.map(r => [r.serial_number || r.equipment_id, r.brand, r.model, r.location, r.customer, r.rental_contract_id, r.start ? fmtDate(r.start) : '', r.end ? fmtDate(r.end) : 'Ongoing'].map(csvEscape).join(','));
        } else if (isCustomers) {
            headers = ['Company', 'Contact', 'Email', 'Phone', 'GSTIN', 'Contracts', 'Status'];
            lines = rows.map(c => [c.company_name, c.contact_person, c.email, c.phone, c.gst_no, contractCountByCustomer[c.id] || 0, c.status].map(csvEscape).join(','));
        } else {
            headers = ['Bill No', 'Customer', 'Bill Date', 'Amount', 'Status', 'Method'];
            lines = rows.map(inv => [inv.bill_no || inv.id, inv.contact_name || inv.customer, inv.bill_date ? fmtDate(inv.bill_date) : (inv.date || ''), Number(inv.amount || 0), (String(inv.status || inv.payment_status || '').toUpperCase() === 'PAID' ? 'Paid' : 'Unpaid'), inv.method || ''].map(csvEscape).join(','));
        }
        if (lines.length === 0) return;
        downloadCsv(`rentals_${view}_${stamp}.csv`, headers, lines);
    };

    // colSpan per view (for skeleton / empty rows inside a table)
    const colCount = isContracts ? 8 : isInvoices ? 7 : isCustomers ? 7 : isMachineContracts ? 7 : 7;

    return (
        <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }} className="animate-fade-in">

            {/* No page title — the nav bar already says "Rentals"; the KPI strip
                below is the first thing on the page and doubles as the switcher.
                Embedded (one view shown inside another page) it is hidden. */}

            {/* KPI / view switcher — compact strip, reads like a second toolbar */}
            {!embedded && !hideViewStrip && (
            <Box sx={{ position: 'relative', mb: 1.75 }}>
                <IconButton 
                    onClick={() => handleScroll('left')}
                    sx={{
                        position: 'absolute', 
                        left: -14, 
                        top: 'calc(50% - 4px)', 
                        transform: 'translateY(-50%)',
                        zIndex: 2, 
                        bgcolor: 'background.paper', 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                        border: '1px solid rgba(148,163,184,0.16)',
                        '&:hover': { bgcolor: 'background.paper', borderColor: 'primary.main', color: 'primary.main' },
                        width: 26,
                        height: 26,
                        display: { xs: 'none', md: 'flex' }
                    }}
                    size="small"
                >
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>

                <Box
                    ref={scrollRef}
                    sx={{
                        display: 'flex',
                        gap: 0.875,
                        overflowX: 'auto',
                        pb: 0.75,
                        mx: { md: 1.5 },
                        '&::-webkit-scrollbar': { height: 6 },
                        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(148, 163, 184, 0.3)', borderRadius: 3 },
                        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                        scrollBehavior: 'smooth'
                    }}
                >
                    {kpiCards.map((kpi) => {
                        const active = kpi.view && view === kpi.view;
                        return (
                            <Card
                                key={kpi.key}
                                onClick={() => switchView(kpi.view)}
                                sx={{
                                    flex: '0 0 auto',
                                    cursor: kpi.view ? 'pointer' : 'default',
                                    borderRadius: '11px',
                                    border: active ? `1.5px solid ${kpi.color}` : '1px solid rgba(148,163,184,0.16)',
                                    boxShadow: active ? `0 2px 10px ${kpi.color}30` : 'none',
                                    bgcolor: active ? `${kpi.color}0d` : 'background.paper',
                                    transition: 'border-color .18s ease, box-shadow .18s ease, background-color .18s ease',
                                    '&:hover': kpi.view ? { borderColor: kpi.color } : undefined,
                                }}
                            >
                                {/* One compact row: small icon · count · label — nav-bar sized. */}
                                <CardContent sx={{ p: '6px 10px !important', display: 'flex', alignItems: 'center', gap: 0.875 }}>
                                    <Avatar variant="rounded" sx={{ bgcolor: `${kpi.color}1a`, color: kpi.color, width: 20, height: 20, borderRadius: '6px' }}>
                                        {React.cloneElement(kpi.icon, { sx: { fontSize: 13 } })}
                                    </Avatar>
                                    <Typography sx={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '0.875rem', lineHeight: 1, color: active ? kpi.color : 'text.primary' }}>
                                        {showSkeleton ? <Skeleton width={20} /> : (typeof kpi.value === 'number' ? Number(kpi.value).toLocaleString('en-IN') : kpi.value)}
                                    </Typography>
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.6875rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                        {kpi.label}
                                    </Typography>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>

                <IconButton 
                    onClick={() => handleScroll('right')}
                    sx={{
                        position: 'absolute', 
                        right: -14, 
                        top: 'calc(50% - 4px)', 
                        transform: 'translateY(-50%)',
                        zIndex: 2, 
                        bgcolor: 'background.paper', 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                        border: '1px solid rgba(148,163,184,0.16)',
                        '&:hover': { bgcolor: 'background.paper', borderColor: 'primary.main', color: 'primary.main' },
                        width: 26,
                        height: 26,
                        display: { xs: 'none', md: 'flex' }
                    }}
                    size="small"
                >
                    <ChevronRightIcon fontSize="small" />
                </IconButton>
            </Box>
            )}

            {/* Rental Daily Report — the default landing view */}
            {view === 'dailyReport' ? (
                <DailyReportPage embedded forcedTeam="rental" />
            ) : (
            <>
            {/* ===== Invoice Download filter bar =====
                Period + machine scope. "All machines" lists every machine that was
                on rent inside the period; "Selected machines" narrows it to the
                machines picked below. */}
            {isInvoiceDownload && (
                /* overflow visible so the machine-search results can drop out of the card */
                <Card sx={{ borderRadius: '16px', p: { xs: 1.75, md: 2 }, mb: 1.75, overflow: 'visible' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
                        {/* What to produce — chosen BEFORE the dates, because it
                            decides which document the Export button generates. */}
                        <ToggleButtonGroup
                            exclusive size="small" value={dlKind}
                            onChange={(e, v) => { if (v) setDlKind(v); }}
                            sx={{ height: '38px', '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 1.75, borderRadius: '10px !important', borderColor: 'rgba(148,163,184,.4)' } }}
                        >
                            <ToggleButton value="invoice">Invoice</ToggleButton>
                            <ToggleButton value="log">Log</ToggleButton>
                        </ToggleButtonGroup>
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, my: 0.5 }} />
                        <TextField
                            size="small" type="date" label="From" value={invFrom}
                            onChange={(e) => setInvFrom(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: { xs: '100%', sm: 172 } }}
                            InputProps={{ sx: { borderRadius: '12px', height: '38px', bgcolor: 'background.paper' } }}
                        />
                        <TextField
                            size="small" type="date" label="To" value={invTo}
                            onChange={(e) => setInvTo(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: { xs: '100%', sm: 172 } }}
                            InputProps={{ sx: { borderRadius: '12px', height: '38px', bgcolor: 'background.paper' } }}
                        />
                        <ToggleButtonGroup
                            exclusive size="small" value={invScope}
                            onChange={(e, v) => { if (v) setInvScope(v); }}
                            sx={{ height: '38px', '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 1.75, borderRadius: '10px !important', borderColor: 'rgba(148,163,184,.4)' } }}
                        >
                            <ToggleButton value="all">All machines</ToggleButton>
                            <ToggleButton value="selected">Selected machines</ToggleButton>
                        </ToggleButtonGroup>

                        {invScope === 'selected' && (
                            <ClickAwayListener onClickAway={() => setInvSearchOpen(false)}>
                                <Box sx={{ position: 'relative', width: { xs: '100%', sm: 340 } }}>
                                    <TextField
                                        size="small" fullWidth
                                        placeholder="Search by serial no., model, brand, customer, site…"
                                        value={invSearch}
                                        onChange={(e) => { setInvSearch(e.target.value); setInvSearchOpen(true); }}
                                        onFocus={() => setInvSearchOpen(true)}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled' }} /></InputAdornment>,
                                            sx: { borderRadius: '12px', bgcolor: 'background.paper', height: '38px' },
                                        }}
                                    />
                                    {invSearchOpen && invSearch.trim() !== '' && (
                                        <Paper elevation={8} sx={{ position: 'absolute', zIndex: 30, top: 'calc(100% + 6px)', left: 0, right: 0, maxHeight: 340, overflowY: 'auto', borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                                            {invSearchResults.sites.length === 0 && invSearchResults.machines.length === 0 && (
                                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                                    <Typography variant="body2" color="text.disabled">Nothing matches “{invSearch}”</Typography>
                                                </Box>
                                            )}

                                            {/* SITES — picking one follows the dates, so the machine
                                                list re-evaluates whenever From/To changes. */}
                                            {invSearchResults.sites.length > 0 && (
                                                <Typography sx={{ px: 1.5, pt: 1, pb: 0.5, fontSize: 10, fontWeight: 800, letterSpacing: '.08em', color: 'text.disabled' }}>
                                                    SITES
                                                </Typography>
                                            )}
                                            {invSearchResults.sites.map((s) => {
                                                const added = invSelectedSites.some(x => normSite(x) === normSite(s.site));
                                                const worked = invRentalRows.filter(r => normSite(r.location) === normSite(s.site)).length;
                                                return (
                                                    <Box key={`site-${s.site}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                                        <LocationIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                                                        <Box minWidth={0} flex={1}>
                                                            <Typography variant="body2" fontWeight={700} noWrap title={s.site}>{s.site}</Typography>
                                                            <Typography variant="caption" color="text.disabled" noWrap component="div">
                                                                {[s.customer, `${worked} machine${worked === 1 ? '' : 's'} in this period`].filter(Boolean).join(' · ')}
                                                            </Typography>
                                                        </Box>
                                                        <Button
                                                            size="small"
                                                            variant={added ? 'outlined' : 'contained'}
                                                            color={added ? 'error' : 'primary'}
                                                            startIcon={added ? <RemoveCircleIcon /> : <AddCircleIcon />}
                                                            onClick={() => toggleInvSite(s.site)}
                                                            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '9px', minWidth: 96 }}
                                                        >
                                                            {added ? 'Remove' : 'Add site'}
                                                        </Button>
                                                    </Box>
                                                );
                                            })}

                                            {invSearchResults.machines.length > 0 && (
                                                <Typography sx={{ px: 1.5, pt: 1, pb: 0.5, fontSize: 10, fontWeight: 800, letterSpacing: '.08em', color: 'text.disabled' }}>
                                                    MACHINES
                                                </Typography>
                                            )}
                                            {invSearchResults.machines.map((m) => {
                                                const added = invSelected.includes(m.equipment_id);
                                                const sub = [m.brand, m.model].filter(Boolean).join(' · ');
                                                // Show customer + site too, so a row matched on either is
                                                // self-explanatory rather than looking like a stray hit.
                                                const onRent = Boolean(m.customer || m.rental_contract_id);
                                                const detail = [sub || m.equipment_id, m.customer, m.location]
                                                    .filter(Boolean).join(' · ')
                                                    + (onRent ? '' : ' · Not on rent in this period');
                                                return (
                                                    <Box key={m.equipment_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}>
                                                        <Box minWidth={0} flex={1}>
                                                            <Typography variant="body2" fontWeight={700} noWrap>{m.serial_number || m.equipment_id}</Typography>
                                                            <Typography variant="caption" color="text.disabled" noWrap component="div" title={detail}>
                                                                {detail}
                                                            </Typography>
                                                        </Box>
                                                        <Button
                                                            size="small"
                                                            variant={added ? 'outlined' : 'contained'}
                                                            color={added ? 'error' : 'primary'}
                                                            startIcon={added ? <RemoveCircleIcon /> : <AddCircleIcon />}
                                                            onClick={() => toggleInvMachine(m.equipment_id)}
                                                            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '9px', minWidth: 96 }}
                                                        >
                                                            {added ? 'Remove' : 'Add'}
                                                        </Button>
                                                    </Box>
                                                );
                                            })}
                                        </Paper>
                                    )}

                                </Box>
                            </ClickAwayListener>
                        )}
                    </Stack>

                    {/* Picked sites + machines */}
                    {invScope === 'selected' && (
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                            {invSelected.length === 0 && invSelectedSites.length === 0 ? (
                                <Typography variant="body2" color="text.disabled">
                                    Nothing selected yet — search a <b>site</b> or a <b>machine</b> above and press <b>Add</b>.
                                    Pick a site and every machine that worked there in the chosen dates is included automatically.
                                    Switch to “All machines” to list everything on rent in this period.
                                </Typography>
                            ) : (
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    {invSelectedSites.map((s) => {
                                        const n = invSiteRows.filter(r => normSite(r.location) === normSite(s)).length;
                                        return (
                                            <Chip
                                                key={`c-site-${s}`} size="small"
                                                icon={<LocationIcon sx={{ fontSize: '15px !important' }} />}
                                                label={`${s} · ${n} machine${n === 1 ? '' : 's'}`}
                                                onDelete={() => toggleInvSite(s)}
                                                sx={{ fontWeight: 700, borderRadius: '8px', bgcolor: 'rgba(13,148,136,.10)', color: '#0f766e' }}
                                            />
                                        );
                                    })}
                                    {invSelected.map((id) => {
                                        const m = invCatalogue.find(x => x.equipment_id === id);
                                        return (
                                            <Chip
                                                key={id} size="small"
                                                label={(m && m.serial_number) || id}
                                                onDelete={() => toggleInvMachine(id)}
                                                sx={{ fontWeight: 700, borderRadius: '8px', bgcolor: ACCENT_SOFT, color: 'primary.main' }}
                                            />
                                        );
                                    })}
                                    <Button size="small" onClick={() => { setInvSelected([]); setInvSelectedSites([]); }} sx={{ textTransform: 'none', fontWeight: 700 }}>
                                        Clear all
                                    </Button>
                                </Stack>
                            )}
                            {invSelectedSites.length > 0 && (
                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                                    Site selections follow the dates — change From/To and the machines listed below update automatically.
                                </Typography>
                            )}
                        </Box>
                    )}
                </Card>
            )}

            {/* Toolbar: view title + count + actions & search */}
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1.5} sx={{ mb: 1.75 }}>
                <Box display="flex" alignItems="center" gap={1.25}>
                    {/* A pinned view (no card strip) is named by the top bar — no title. */}
                    {!hideViewStrip && <Typography variant="h6" fontWeight={700}>{TITLES[view]}</Typography>}
                    <Box component="span" sx={{ fontWeight: 600, fontSize: 12, color: 'text.disabled', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', px: 1.1, py: 0.4, borderRadius: '8px' }}>
                        {showSkeleton ? '—' : total}
                    </Box>
                    {/* Removals are never silent — always visible and reversible. */}
                    {isInvoiceDownload && invRemoved.length > 0 && (
                        <Chip
                            size="small"
                            icon={<RemoveCircleIcon sx={{ fontSize: '15px !important' }} />}
                            label={`${invRemoved.length} removed`}
                            onDelete={() => setInvRemoved([])}
                            deleteIcon={<Tooltip title="Restore removed machines"><UndoIcon sx={{ fontSize: '15px !important' }} /></Tooltip>}
                            sx={{ fontWeight: 700, borderRadius: '8px', bgcolor: 'rgba(239,68,68,.10)', color: 'error.main' }}
                        />
                    )}
                </Box>
                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    {/* In the Invoice Download view this button downloads the usage
                        PDFs themselves; everywhere else it exports the list as CSV. */}
                    <Button variant={isInvoiceDownload ? 'contained' : 'outlined'} color="inherit"
                        startIcon={<DownloadIcon />}
                        onClick={isInvoiceDownload ? openInvoiceDownload : handleExportLogs}
                        sx={isInvoiceDownload
                            ? { bgcolor: '#059669', color: '#fff', fontWeight: 700, height: '38px', textTransform: 'none', borderRadius: '10px', '&:hover': { bgcolor: '#047857' } }
                            : { borderColor: 'rgba(148,163,184,.4)', color: 'text.secondary', fontWeight: 600, height: '38px', textTransform: 'none', borderRadius: '10px' }}>
                        {isInvoiceDownload ? 'Export Logs / Invoices' : 'Export Logs'}
                    </Button>
                    {isInvoiceDownload && (
                        <Button variant="outlined" color="inherit" onClick={handleExportLogs}
                            sx={{ borderColor: 'rgba(148,163,184,.4)', color: 'text.secondary', fontWeight: 600, height: '38px', textTransform: 'none', borderRadius: '10px' }}>
                            List CSV
                        </Button>
                    )}
                    {isCustomers && (
                        <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={() => setShowAddCustomerModal(true)}
                            sx={{ borderColor: 'rgba(236,72,153,.5)', color: '#db2777', fontWeight: 600, height: '38px', textTransform: 'none', borderRadius: '10px', '&:hover': { borderColor: '#db2777', bgcolor: 'rgba(236,72,153,.04)' } }}>
                            Add Customer
                        </Button>
                    )}
                    {isContracts && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowNewContractModal(true)}
                            sx={{ fontWeight: 700, height: '38px', textTransform: 'none', borderRadius: '10px' }}>
                            New Contract
                        </Button>
                    )}
                    {isMachineContracts && (
                        <Button variant="contained" startIcon={<AddIcon />}
                            onClick={() => { setMcForm(BLANK_MC); setShowAddMachineContract(true); }}
                            sx={{ fontWeight: 700, height: '38px', textTransform: 'none', borderRadius: '10px' }}>
                            Add machine contract
                        </Button>
                    )}
                    <TextField
                        size="small"
                        placeholder="Search rentals, customers, invoices…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ width: { xs: '100%', sm: 260 } }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled' }} /></InputAdornment>,
                            sx: { borderRadius: '12px', bgcolor: 'background.paper', height: '38px' },
                        }}
                    />
                </Stack>
            </Stack>

            {/* Table card */}
            <TableContainer component={Card} sx={{ borderRadius: '16px' }}>
                <Table>
                    <TableHead>
                        {isContracts ? (
                            <TableRow>
                                <TableCell>Contract</TableCell>
                                <TableCell>Customer</TableCell>
                                <TableCell>Site location</TableCell>
                                <TableCell>Units</TableCell>
                                <TableCell>Start</TableCell>
                                <TableCell>End</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell />
                            </TableRow>
                        ) : isMachines ? (
                            <TableRow>
                                <TableCell>Equipment</TableCell>
                                <TableCell>Serial No.</TableCell>
                                <TableCell>Brand</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell />
                            </TableRow>
                        ) : isCustomers ? (
                            <TableRow>
                                <TableCell>Company</TableCell>
                                <TableCell>Contact person</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Contracts</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell />
                            </TableRow>
                        ) : isInvoiceDownload ? (
                            <TableRow>
                                <TableCell>Serial No.</TableCell>
                                <TableCell>Brand</TableCell>
                                <TableCell>Model</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Customer</TableCell>
                                <TableCell>On rent</TableCell>
                                <TableCell>Contract</TableCell>
                            </TableRow>
                        ) : isMachineContracts ? (
                            <TableRow>
                                <TableCell>Machine Contract ID</TableCell>
                                <TableCell>Machine</TableCell>
                                <TableCell>Contract</TableCell>
                                <TableCell>From</TableCell>
                                <TableCell>To</TableCell>
                                <TableCell>Shift</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        ) : (
                            <TableRow>
                                <TableCell>Bill no.</TableCell>
                                <TableCell>Customer</TableCell>
                                <TableCell>Bill date</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Method</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        )}
                    </TableHead>
                    <TableBody>
                        {showSkeleton ? [...Array(6)].map((_, i) => (
                            <TableRow key={i}><TableCell colSpan={colCount}><Skeleton height={42} sx={{ my: 0.5 }} /></TableCell></TableRow>
                        )) : (
                            <>
                                {/* CONTRACTS */}
                                {isContracts && pagedRows.map((c, idx) => {
                                    const label = contractStatusLabel(c.contract_status, view === 'completed' ? 'Completed' : 'Active');
                                    const openEnd = !c.contract_end;
                                    return (
                                        <TableRow key={c.rental_contract_id || idx} hover sx={{ cursor: 'pointer' }}
                                            onClick={() => goDetail(view === 'contractData'
                                                ? `/contract-data/${c.rental_contract_id}`
                                                : `/contract-live/${c.rental_contract_id}`)}>
                                            <TableCell>
                                                {/* Contract number opens the contract DETAILS (info + machine
                                                    contracts + Edit); the rest of the row opens the live view.
                                                    In the Contract Details view everything opens the raw-data page. */}
                                                <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                                                    onClick={(e) => { e.stopPropagation(); goDetail(view === 'contractData' ? `/contract-data/${c.rental_contract_id}` : `/contract/${c.rental_contract_id}`); }}>
                                                    {c.rental_contract_id || '—'}
                                                </Typography>
                                                <Typography variant="caption" color="text.disabled">{rentalTypeLabel(c.rental_type)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1.25}>
                                                    <Avatar variant="rounded" sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: ACCENT_SOFT, color: 'primary.main', fontFamily: 'Outfit', fontWeight: 800, fontSize: 12 }}>
                                                        {initials(c.company_name)}
                                                    </Avatar>
                                                    <Typography variant="body2" fontWeight={700}>{c.company_name || 'Unassigned'}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <LocationIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                                    <Typography variant="body2" color="text.secondary">{c.site_location || '—'}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box component="span" sx={{ display: 'inline-block', px: 1.25, py: 0.375, borderRadius: '8px', bgcolor: ACCENT_SOFT, color: 'primary.main', fontWeight: 800, fontSize: 11.5 }}>
                                                    {c.device_count || 0} units
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>{fmtDate(c.contract_start)}</TableCell>
                                            <TableCell sx={{ color: openEnd ? 'text.disabled' : 'text.secondary', fontWeight: 500 }}>{openEnd ? 'Open-end' : fmtDate(c.contract_end)}</TableCell>
                                            <TableCell><Pill s={CONTRACT_STATUS[label] || NEUTRAL_PILL} label={label} /></TableCell>
                                            <TableCell align="right" sx={{ color: 'text.disabled' }}><ChevronRightIcon sx={{ fontSize: 20, verticalAlign: 'middle' }} /></TableCell>
                                        </TableRow>
                                    );
                                })}

                                {/* INVOICE DOWNLOAD — machines on rent in the selected period */}
                                {isInvoiceDownload && pagedRows.map((r, idx) => (
                                    <TableRow key={r.key || idx} hover sx={{ cursor: 'pointer' }}
                                        title={dlKind === 'log' ? 'Open this machine\'s log' : 'Open this machine\'s invoice for these dates'}
                                        onClick={() => openMachineDoc(r)}>
                                        <TableCell sx={{ fontWeight: 800 }}>
                                            {r.serial_number || '—'}
                                            <Typography variant="caption" color="text.disabled" component="div">{r.equipment_id}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>{r.brand || '—'}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{r.model || '—'}</TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <LocationIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                                <Typography variant="body2" color="text.secondary">{r.location || '—'}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {r.customer ? (
                                                <Box display="flex" alignItems="center" gap={1.25}>
                                                    <Avatar variant="rounded" sx={{ width: 30, height: 30, borderRadius: '9px', bgcolor: ACCENT_SOFT, color: 'primary.main', fontFamily: 'Outfit', fontWeight: 800, fontSize: 11 }}>
                                                        {initials(r.customer)}
                                                    </Avatar>
                                                    <Typography variant="body2" fontWeight={700}>{r.customer}</Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.disabled">Not on rent in this period</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                            {r.start ? fmtDate(r.start) : '—'} → {r.end ? fmtDate(r.end) : 'Ongoing'}
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: r.rental_contract_id ? 'primary.main' : 'text.disabled' }}
                                                    onClick={(e) => { if (r.rental_contract_id) { e.stopPropagation(); goDetail(`/contract/${r.rental_contract_id}`); } }}>
                                                    {r.rental_contract_id || '—'}
                                                </Typography>
                                                {/* Drop this machine from the list (and from the export) */}
                                                <Tooltip title="Remove this machine from the list">
                                                    <IconButton size="small" aria-label={`Remove ${r.serial_number || r.equipment_id} from the list`}
                                                        onClick={(e) => { e.stopPropagation(); removeInvRow(r); }}
                                                        sx={{ color: 'text.disabled', '&:hover': { color: 'error.main', bgcolor: 'rgba(239,68,68,.08)' } }}>
                                                        <RemoveCircleIcon sx={{ fontSize: 17 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {/* MACHINE CONTRACTS — every ced line, styled */}
                                {isMachineContracts && pagedRows.map((mc, idx) => {
                                    const st = String(mc.status || '').toUpperCase();
                                    const stC = st === 'ACTIVE' ? '#16a34a' : st === 'AMENDED' ? '#d97706' : st === 'CANCELLED' ? '#dc2626' : st === 'COMPLETED' ? '#2563eb' : '#6b7280';
                                    let shiftLabels = [];
                                    try {
                                        const arr = typeof mc.shifts === 'string' ? JSON.parse(mc.shifts) : mc.shifts;
                                        if (Array.isArray(arr) && arr.length) shiftLabels = arr.map(s => `${String(s.start_time).slice(0, 5)}–${String(s.end_time).slice(0, 5)}`);
                                    } catch (e) { /* fall back below */ }
                                    if (!shiftLabels.length && (mc.shift_start_time || mc.shift_end_time)) {
                                        shiftLabels = [`${String(mc.shift_start_time || '').slice(0, 5)}–${String(mc.shift_end_time || '').slice(0, 5)}`];
                                    }
                                    return (
                                        <TableRow key={mc.id || idx} hover sx={{ cursor: 'pointer', ...(st !== 'ACTIVE' ? { bgcolor: 'rgba(148,163,184,0.06)' } : {}) }}
                                            onClick={() => goDetail(`/contract-data/${mc.rental_contract_id}?machine=${encodeURIComponent(mc.equipment_id || '')}`)}>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main' }}>{mc.sub_contract_id || '—'}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={700}>{mc.serial_number || '—'}</Typography>
                                                {(mc.model_number || mc.brand) && (
                                                    <Typography variant="caption" color="text.disabled">
                                                        {[mc.model_number, mc.brand].filter(Boolean).join(' · ')}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell><Typography variant="body2" fontWeight={600}>{mc.rental_contract_id}</Typography></TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>{fmtDate(mc.effective_from_date)}</TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>{mc.effective_to_date ? fmtDate(mc.effective_to_date) : 'Ongoing'}</TableCell>
                                            <TableCell>
                                                <Stack spacing={0.5} alignItems="flex-start">
                                                    {shiftLabels.length ? shiftLabels.map((s, i) => (
                                                        <Box key={i} component="span" sx={{ px: 1, py: 0.25, borderRadius: '7px', bgcolor: '#f1f5f9', fontWeight: 600, fontSize: 11.5, color: 'text.secondary' }}>
                                                            S{i + 1} {s}
                                                        </Box>
                                                    )) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Box component="span" sx={{ px: 1.125, py: 0.375, borderRadius: '8px', bgcolor: `${stC}15`, color: stC, fontWeight: 800, fontSize: 11 }}>{st || '—'}</Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}

                                {/* MACHINES */}
                                {isMachines && pagedRows.map((m, idx) => {
                                    const cat = categoryStyle(m.category);
                                    const CatIcon = cat.Icon;
                                    return (
                                        <TableRow key={m.assetId || idx} hover sx={{ cursor: 'pointer' }}
                                            onClick={() => openMachine(m)}>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1.5}>
                                                    <Avatar variant="rounded" sx={{ width: 38, height: 38, borderRadius: '11px', bgcolor: `${cat.color}18`, color: cat.color }}>
                                                        <CatIcon sx={{ fontSize: 20 }} />
                                                    </Avatar>
                                                    <Box minWidth={0}>
                                                        <Typography variant="body2" fontWeight={700} noWrap>{m.model}</Typography>
                                                        <Typography variant="caption" color="text.disabled">{m.sub}</Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>
                                                {m.serial_number || '—'}
                                            </TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>{m.brand}</TableCell>
                                            <TableCell>
                                                <Box component="span" sx={{ display: 'inline-block', px: 1.25, py: 0.375, borderRadius: '8px', bgcolor: `${cat.color}18`, color: cat.color, fontWeight: 700, fontSize: 11 }}>
                                                    {m.category}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <LocationIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                                    <Typography variant="body2" color="text.secondary">{m.place}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell><Pill s={MACHINE_STATUS[m.status] || NEUTRAL_PILL} label={m.status} /></TableCell>
                                            <TableCell align="right" sx={{ color: 'text.disabled', whiteSpace: 'nowrap' }}>
                                                <ChevronRightIcon sx={{ fontSize: 20, verticalAlign: 'middle' }} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}

                                {/* CUSTOMERS */}
                                {isCustomers && pagedRows.map((c, idx) => {
                                    const label = String(c.status).toUpperCase() === 'ACTIVE' ? 'Active' : 'Inactive';
                                    return (
                                        <TableRow key={c.id || idx} hover sx={{ cursor: 'pointer' }} onClick={() => openCustomer(c)}>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1.25}>
                                                    <Avatar variant="rounded" sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: '#fce7f3', color: '#db2777', fontFamily: 'Outfit', fontWeight: 800, fontSize: 12 }}>
                                                        {initials(c.company_name)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={700}>{c.company_name}</Typography>
                                                        {c.gst_no && <Typography variant="caption" color="text.disabled">GSTIN {c.gst_no}</Typography>}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>{c.contact_person || '—'}</TableCell>
                                            <TableCell sx={{ color: 'primary.main', fontWeight: 500 }}>{c.email || '—'}</TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>{c.phone || '—'}</TableCell>
                                            <TableCell>
                                                <Box component="span" sx={{ display: 'inline-block', px: 1.25, py: 0.375, borderRadius: '8px', bgcolor: ACCENT_SOFT, color: 'primary.main', fontWeight: 800, fontSize: 11.5 }}>
                                                    {contractCountByCustomer[c.id] || 0}
                                                </Box>
                                            </TableCell>
                                            <TableCell><Pill s={CUSTOMER_STATUS[label] || NEUTRAL_PILL} label={label} /></TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" aria-label="Edit customer" onClick={(e) => { e.stopPropagation(); openCustomer(c); setEditMode(true); }}>
                                                    <EditIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}

                                {/* INVOICES */}
                                {isInvoices && pagedRows.map((inv, idx) => {
                                    const paid = String(inv.status || inv.payment_status || '').toUpperCase() === 'PAID';
                                    const label = paid ? 'Paid' : 'Unpaid';
                                    const sub = inv.rental_contract_id || inv.contract_id || inv.equipment_id || '';
                                    return (
                                        <TableRow key={inv.invoice_id || inv.bill_no || idx} hover>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 800 }}>{inv.bill_no || inv.id || '—'}</Typography>
                                                {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
                                            </TableCell>
                                            <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>{inv.contact_name || inv.customer || '—'}</TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>{inv.bill_date ? fmtDate(inv.bill_date) : (inv.date || '—')}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 800 }}>{inr(inv.amount)}</TableCell>
                                            <TableCell><Pill s={INVOICE_STATUS[label]} label={label} /></TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>{inv.method || '—'}</TableCell>
                                            <TableCell align="right">
                                                <Button variant="outlined" size="small" startIcon={<EyeIcon />}
                                                    onClick={(e) => { e.stopPropagation(); handleViewInvoice(inv.invoice_id || (inv.id && String(inv.id).replace('INV-', ''))); }}
                                                    sx={{ fontWeight: 700 }}>
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </>
                        )}
                    </TableBody>
                </Table>

                {/* Empty state */}
                {!showSkeleton && total === 0 && (
                    <Box sx={{ p: 7, textAlign: 'center' }}>
                        <SearchOffIcon sx={{ fontSize: 52, color: 'text.disabled' }} />
                        {isInvoiceDownload ? (
                            <>
                                <Typography variant="h6" fontWeight={700} color="text.secondary" sx={{ mt: 1 }}>
                                    {invScope === 'selected' && invSelected.length === 0
                                        ? 'No machine selected'
                                        : 'No machine on rent in this period'}
                                </Typography>
                                <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                                    {invScope === 'selected' && invSelected.length === 0
                                        ? 'Search a machine above and press Add, or switch to “All machines”.'
                                        : `Nothing between ${fmtDate(invFrom)} and ${fmtDate(invTo)} — widen the date range.`}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography variant="h6" fontWeight={700} color="text.secondary" sx={{ mt: 1 }}>Nothing matches your search</Typography>
                                <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>Try a different term or switch the view above.</Typography>
                            </>
                        )}
                    </Box>
                )}

                {/* Footer / pagination */}
                {!showSkeleton && total > 0 && (
                    <Box p={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.disabled' }}>
                            Showing {(page - 1) * ROWS_PER_PAGE + 1}–{Math.min(page * ROWS_PER_PAGE, total)} of {total} records
                        </Typography>
                        {pageCount > 1 && (
                            <Pagination count={pageCount} page={page} onChange={(e, v) => setPage(v)} size="small" shape="rounded" color="primary" />
                        )}
                    </Box>
                )}
            </TableContainer>
            </>
            )}

            {/* One machine's invoice, opened from its row in the Log Sheets list */}
            <PdfPreviewDialog doc={docPreview} onClose={() => setDocPreview(null)} />

            {/* ===== Invoice / work-log download — one usage PDF per machine ===== */}
            <Dialog open={dlOpen} onClose={() => !dlBusy && setDlOpen(false)} PaperProps={{ sx: { borderRadius: '16px', minWidth: 440 } }}>
                <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DownloadIcon sx={{ color: '#059669' }} /> Download Invoices
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {rows.length} machine{rows.length === 1 ? '' : 's'} in the list below. Each one that worked in
                        these dates downloads as its own usage PDF, named by serial number — the same
                        document the contract page produces.
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <TextField label="From" type="date" value={dlFrom} fullWidth
                            onChange={(e) => setDlFrom(e.target.value)} InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                        <TextField label="To" type="date" value={dlTo} fullWidth
                            onChange={(e) => setDlTo(e.target.value)} InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                    </Stack>
                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <TextField label="Leverage (hours)" type="number" value={dlLeverage} fullWidth
                            onChange={(e) => setDlLeverage(e.target.value)}
                            inputProps={{ min: 0, max: 6, step: 0.5 }} InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                        <FormControl fullWidth>
                            <InputLabel id="inv-dl-ot-label">OT Billing</InputLabel>
                            <Select labelId="inv-dl-ot-label" label="OT Billing" value={dlOtBilling}
                                onChange={(e) => setDlOtBilling(e.target.value)} sx={{ borderRadius: '10px' }}>
                                <MenuItem value="actual">Actual</MenuItem>
                                <MenuItem value="30min">30min</MenuItem>
                                <MenuItem value="1h">1h</MenuItem>
                                <MenuItem value="2h">2h</MenuItem>
                                <MenuItem value="5h">5h</MenuItem>
                                <MenuItem value="10h">10h</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mx: 1.5 }}>
                        Grace on the shift start. Machines may start this many hours early or late and finish
                        correspondingly — only work beyond the shifted window counts as extra. 0 = no grace.
                    </Typography>
                    {dlProgress && (
                        <Alert severity={dlProgress.finished ? 'success' : 'info'} sx={{ mt: 2, borderRadius: '10px' }}>
                            {dlProgress.finished
                                ? `Done — downloaded ${dlProgress.saved} PDF${dlProgress.saved === 1 ? '' : 's'}.`
                                : `Checking machines ${dlProgress.done}/${dlProgress.total} — ${dlProgress.saved} PDF${dlProgress.saved === 1 ? '' : 's'} downloaded…`}
                            <br /><Typography variant="caption" color="text.secondary">If your browser asks to allow multiple downloads, click Allow.</Typography>
                        </Alert>
                    )}
                    {dlError && <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>{dlError}</Alert>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDlOpen(false)} disabled={dlBusy} sx={{ textTransform: 'none', fontWeight: 700 }}>Cancel</Button>
                    <Button variant="contained" onClick={handleDownloadInvoices} disabled={dlBusy || !dlFrom || !dlTo}
                        startIcon={dlBusy ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DownloadIcon />}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}>
                        {dlBusy ? 'Preparing…' : 'Download'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ===== Invoice Preview Dialog ===== */}
            <Dialog open={showInvoiceModal} onClose={() => setShowInvoiceModal(false)} maxWidth="lg" fullWidth TransitionComponent={Slide}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={700}>Invoice Preview: {selectedInvoice?.bill_no}</Typography>
                    <IconButton onClick={() => setShowInvoiceModal(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ bgcolor: '#f1f5f9', minHeight: '500px' }}>
                    {modalLoading ? (
                        <Box>
                            <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
                            <Skeleton variant="rectangular" height={100} sx={{ mb: 2 }} />
                            <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
                            <Skeleton variant="rectangular" height={80} />
                        </Box>
                    ) : modalError ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography variant="h6" color="error" gutterBottom>⚠️ Error Loading Invoice</Typography>
                            <Typography color="text.secondary">{modalError}</Typography>
                            <Button variant="outlined" sx={{ mt: 2 }} onClick={() => handleViewInvoice(selectedInvoice?.invoice_id)}>Retry</Button>
                        </Box>
                    ) : templateHtml ? (
                        <Paper sx={{ p: 0, overflow: 'auto' }}>
                            <div dangerouslySetInnerHTML={{ __html: templateHtml }} />
                        </Paper>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography color="text.secondary">No invoice content to display.</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setShowInvoiceModal(false)}>Close</Button>
                    <Button variant="contained" startIcon={<PrintIcon />}
                        onClick={() => window.open(`${process.env.REACT_APP_API_URL}/billing/invoices/${selectedInvoice?.invoice_id}/download`, '_blank')}>Print PDF</Button>
                </DialogActions>
            </Dialog>

            {/* ===== Customer Details / Edit Dialog ===== */}
            <Dialog open={showCustomerModal} onClose={() => setShowCustomerModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={700}>{editMode ? 'Edit Customer' : 'Customer Details'}</Typography>
                    {!editMode ? (
                        <IconButton onClick={() => setEditMode(true)} color="primary"><EditIcon /></IconButton>
                    ) : (
                        <IconButton onClick={() => setShowCustomerModal(false)}><CloseIcon /></IconButton>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Company Name" value={customerForm.company_name} onChange={(e) => setCustomerForm({ ...customerForm, company_name: e.target.value })} disabled={!editMode} InputProps={{ readOnly: !editMode }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Contact Person" value={customerForm.contact_person} onChange={(e) => setCustomerForm({ ...customerForm, contact_person: e.target.value })} disabled={!editMode} InputProps={{ readOnly: !editMode }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="GST No" value={customerForm.gst_no} onChange={(e) => setCustomerForm({ ...customerForm, gst_no: e.target.value })} disabled={!editMode} InputProps={{ readOnly: !editMode }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Email" value={customerForm.email} onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })} disabled={!editMode} InputProps={{ readOnly: !editMode }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Phone" value={customerForm.phone} onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })} disabled={!editMode} InputProps={{ readOnly: !editMode }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth multiline rows={3} label="Address" value={customerForm.address} onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })} disabled={!editMode} InputProps={{ readOnly: !editMode }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => { setShowCustomerModal(false); setEditMode(false); }}>Cancel</Button>
                    {editMode && (
                        <Button variant="contained"
                            onClick={async () => {
                                try {
                                    const res = await mewpService.updateCustomer(selectedCustomer.id, customerForm);
                                    if (res.success) { setShowCustomerModal(false); setEditMode(false); fetchData(); }
                                } catch (error) { alert('Failed to update customer'); }
                            }}>
                            Save Changes
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* ===== New Contract Dialog ===== */}
            <Dialog open={showNewContractModal} onClose={() => setShowNewContractModal(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={700}>Add New Contract</Typography>
                    <IconButton onClick={() => setShowNewContractModal(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Rental Contract ID" placeholder="Auto-generated (RC-2026-…) if left blank"
                                InputLabelProps={{ shrink: true }}
                                helperText="Leave blank to auto-assign the next ID"
                                value={newContractForm.rental_contract_id} onChange={(e) => setNewContractForm({ ...newContractForm, rental_contract_id: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField select fullWidth label="Customer" SelectProps={{ native: true }} value={newContractForm.customer_id} onChange={(e) => setNewContractForm({ ...newContractForm, customer_id: e.target.value })}>
                                <option value=""></option>
                                {customers.map(c => (<option key={c.id} value={c.id}>{c.company_name}</option>))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Site Location" value={newContractForm.site_location} onChange={(e) => setNewContractForm({ ...newContractForm, site_location: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="date" label="Contract Start" InputLabelProps={{ shrink: true }} value={newContractForm.contract_start} onChange={(e) => setNewContractForm({ ...newContractForm, contract_start: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="date" label="Contract End" InputLabelProps={{ shrink: true }} value={newContractForm.contract_end} onChange={(e) => setNewContractForm({ ...newContractForm, contract_end: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField select fullWidth label="Rental Type" SelectProps={{ native: true }} value={newContractForm.rental_type} onChange={(e) => setNewContractForm({ ...newContractForm, rental_type: e.target.value })}>
                                <option value="MONTHLY">Monthly</option>
                                <option value="USAGE_BASED">Usage Based</option>
                            </TextField>
                        </Grid>
                        {/* Machines are deliberately NOT added here. Each machine's
                            line carries its own rental start date, which is what its
                            invoice is billed from — capturing equipment at this point
                            left no place for that date, so it silently inherited the
                            contract start and over-billed machines that reached site
                            later. Add them in Machine Contracts once this is saved. */}
                        <Grid size={{ xs: 12 }}>
                            <Divider sx={{ my: 1 }} />
                            <Alert severity="info" icon={<FleetIcon />} sx={{ borderRadius: '12px' }}>
                                <Typography variant="body2" fontWeight={700} sx={{ mb: 0.25 }}>Machines are added separately</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Save the contract, then open <b>Machine Contracts</b> → <b>Add machine contract</b> to map
                                    each machine to it. Every machine gets its own rental start date there, and that date —
                                    not the contract start — is what its invoice is billed from.
                                </Typography>
                            </Alert>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setShowNewContractModal(false)}>Cancel</Button>
                    <Button variant="contained" disabled={creatingContract}
                        onClick={async () => {
                            if (creatingContract) return;            // block double-click
                            // No equipment here — machines are mapped in Machine Contracts.
                            const payload = { ...newContractForm };
                            if (!payload.customer_id || !payload.contract_start) {
                                alert('Please choose a Customer and a Contract Start date.');
                                return;
                            }
                            try {
                                setCreatingContract(true);
                                const res = await mewpService.createRentalContract(payload);
                                if (res.success) {
                                    setShowNewContractModal(false);
                                    setNewContractForm({ rental_contract_id: '', customer_id: '', contract_start: '', contract_end: '', rental_type: 'MONTHLY', site_location: '' });
                                    fetchData();
                                    setSnack('Contract created. Add its machines in Machine Contracts.');
                                }
                            } catch (error) { console.error(error); alert('Failed to create contract'); }
                            finally { setCreatingContract(false); }
                        }}>
                        {creatingContract ? 'Saving…' : 'Save Contract'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ===== Add Machine Contract — map a machine onto a contract =====
                The machine's own rental start date is captured here and is what
                its monthly invoice is billed from, so a machine that reaches site
                after the contract was signed is never charged from day one. */}
            <Dialog open={showAddMachineContract} onClose={() => !savingMachineContract && setShowAddMachineContract(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <FleetIcon sx={{ color: 'primary.main' }} />
                        <Typography variant="h6" fontWeight={700}>Add machine contract</Typography>
                    </Box>
                    <IconButton onClick={() => setShowAddMachineContract(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 0.5 }}>
                        <TextField select fullWidth label="Contract" SelectProps={{ native: true }} required
                            value={mcForm.rental_contract_id}
                            onChange={(e) => setMcForm({ ...mcForm, rental_contract_id: e.target.value })}>
                            <option value="">Select contract…</option>
                            {allContracts.map(c => (
                                <option key={c.rental_contract_id} value={c.rental_contract_id}>
                                    {c.rental_contract_id}{c.company_name ? ` · ${c.company_name}` : ''}
                                </option>
                            ))}
                        </TextField>

                        <Autocomplete
                            options={machines}
                            value={machines.find(m => m.assetId === mcForm.equipment_id) || null}
                            onChange={(e, v) => setMcForm({ ...mcForm, equipment_id: v ? v.assetId : '' })}
                            getOptionLabel={(m) => m ? `${m.serial_number || m.assetId}${m.model ? ` · ${m.model}` : ''}` : ''}
                            isOptionEqualToValue={(o, v) => o.assetId === v.assetId}
                            renderInput={(params) => (
                                <TextField {...params} label="Machine" required
                                    placeholder="Search serial no., model, brand…"
                                    helperText="Identified by serial number" />
                            )}
                        />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField fullWidth type="date" required label="Machine rental start"
                                InputLabelProps={{ shrink: true }} value={mcForm.effective_from_date}
                                onChange={(e) => setMcForm({ ...mcForm, effective_from_date: e.target.value })}
                                helperText="Billed from this date — not the contract start" />
                            <TextField fullWidth type="date" label="Rental end (blank = ongoing)"
                                InputLabelProps={{ shrink: true }} value={mcForm.effective_to_date}
                                onChange={(e) => setMcForm({ ...mcForm, effective_to_date: e.target.value })}
                                helperText="Defaults to the contract end" />
                        </Stack>

                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: 12, color: 'text.secondary', mb: 1 }}>Shifts</Typography>
                            <Stack spacing={1.25}>
                                {mcForm.shifts.map((s, i) => (
                                    <Stack key={i} direction="row" spacing={1.25} alignItems="center">
                                        <TextField size="small" type="time" label={`S${i + 1} start`} value={s.start_time}
                                            onChange={(e) => setMcForm(f => { const sh = [...f.shifts]; sh[i] = { ...sh[i], start_time: e.target.value }; return { ...f, shifts: sh }; })}
                                            InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                                        <TextField size="small" type="time" label={`S${i + 1} end`} value={s.end_time}
                                            onChange={(e) => setMcForm(f => { const sh = [...f.shifts]; sh[i] = { ...sh[i], end_time: e.target.value }; return { ...f, shifts: sh }; })}
                                            InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                                        <Button size="small" color="error" disabled={mcForm.shifts.length <= 1}
                                            onClick={() => setMcForm(f => ({ ...f, shifts: f.shifts.filter((_, j) => j !== i) }))}>
                                            Remove
                                        </Button>
                                    </Stack>
                                ))}
                                <Button size="small" disabled={mcForm.shifts.length >= 3}
                                    onClick={() => setMcForm(f => ({ ...f, shifts: [...f.shifts, { start_time: '20:00', end_time: '06:00' }] }))}
                                    sx={{ alignSelf: 'flex-start', fontWeight: 700 }}>
                                    + Add shift
                                </Button>
                            </Stack>
                        </Box>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField fullWidth size="small" type="number" label="Monthly Rate" value={mcForm.monthly_rate}
                                onChange={(e) => setMcForm({ ...mcForm, monthly_rate: e.target.value })} />
                            <TextField fullWidth size="small" type="number" label="Hourly Rate" value={mcForm.hourly_rate}
                                onChange={(e) => setMcForm({ ...mcForm, hourly_rate: e.target.value })} />
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField fullWidth size="small" type="number" label="Extra Hours Rate" value={mcForm.extra_hours_rate}
                                onChange={(e) => setMcForm({ ...mcForm, extra_hours_rate: e.target.value })} />
                            <TextField fullWidth size="small" type="number" label="Advance" value={mcForm.advance_payment}
                                onChange={(e) => setMcForm({ ...mcForm, advance_payment: e.target.value })} />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setShowAddMachineContract(false)} disabled={savingMachineContract}>Cancel</Button>
                    <Button variant="contained" disabled={savingMachineContract}
                        onClick={async () => {
                            if (savingMachineContract) return;
                            if (!mcForm.rental_contract_id || !mcForm.equipment_id || !mcForm.effective_from_date) {
                                setSnack('Contract, machine and the machine rental start date are required.');
                                return;
                            }
                            if (mcForm.effective_to_date && mcForm.effective_to_date < mcForm.effective_from_date) {
                                setSnack('Rental end cannot be before the rental start.');
                                return;
                            }
                            try {
                                setSavingMachineContract(true);
                                const res = await mewpService.createMachineContractRow({
                                    ...mcForm,
                                    effective_to_date: mcForm.effective_to_date || null,
                                });
                                if (res.success) {
                                    setShowAddMachineContract(false);
                                    setMcForm(BLANK_MC);
                                    setSnack(`Added ${res.row?.sub_contract_id || 'machine contract'}.`);
                                    fetchData();
                                }
                            } catch (e) {
                                // Prefer the server's message ("X is already on RC-…"),
                                // not axios's generic "Request failed with status code".
                                setSnack(e?.response?.data?.error || e?.message || 'Failed to add the machine contract.');
                            } finally { setSavingMachineContract(false); }
                        }}
                        sx={{ fontWeight: 700 }}>
                        {savingMachineContract ? 'Saving…' : 'Add machine contract'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack('')}
                message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />

            {/* ===== Add New Customer Dialog ===== */}
            <Dialog open={showAddCustomerModal} onClose={() => setShowAddCustomerModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <PersonAddIcon sx={{ color: '#db2777' }} />
                        <Typography variant="h6" fontWeight={700}>Add New Customer</Typography>
                    </Box>
                    <IconButton onClick={() => setShowAddCustomerModal(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Company Name *" value={newCustomerForm.company_name} onChange={(e) => setNewCustomerForm({ ...newCustomerForm, company_name: e.target.value })} placeholder="Enter company name" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Contact Person" value={newCustomerForm.contact_person} onChange={(e) => setNewCustomerForm({ ...newCustomerForm, contact_person: e.target.value })} placeholder="Enter contact person" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="GST No" value={newCustomerForm.gst_no} onChange={(e) => setNewCustomerForm({ ...newCustomerForm, gst_no: e.target.value })} placeholder="Enter GST number" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Email" type="email" value={newCustomerForm.email} onChange={(e) => setNewCustomerForm({ ...newCustomerForm, email: e.target.value })} placeholder="Enter email address" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Phone" value={newCustomerForm.phone} onChange={(e) => setNewCustomerForm({ ...newCustomerForm, phone: e.target.value })} placeholder="Enter phone number" />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth multiline rows={3} label="Address" value={newCustomerForm.address} onChange={(e) => setNewCustomerForm({ ...newCustomerForm, address: e.target.value })} placeholder="Enter full address" />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setShowAddCustomerModal(false)}>Cancel</Button>
                    <Button variant="contained" disabled={savingNewCustomer || !newCustomerForm.company_name.trim()}
                        onClick={async () => {
                            setSavingNewCustomer(true);
                            try {
                                const res = await mewpService.createCustomer(newCustomerForm);
                                if (res.success) {
                                    setShowAddCustomerModal(false);
                                    setNewCustomerForm({ company_name: '', contact_person: '', email: '', phone: '', address: '', gst_no: '', status: 'ACTIVE' });
                                    fetchData();
                                }
                            } catch (error) { console.error('Create customer error:', error); alert('Failed to create customer. Please try again.'); }
                            setSavingNewCustomer(false);
                        }}
                        sx={{ bgcolor: '#db2777', '&:hover': { bgcolor: '#be185d' } }}>
                        {savingNewCustomer ? 'Saving...' : 'Save Customer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RentalPage;
